/* A server agent's runs on the Traces page — the one place the wire meets the
   mockup's trace model, the way `shared/api/threads.ts::toChat` is for chats.

   `toTraceRun` makes an `ApiRun` the page's `Run`, carrying the wire as
   `recorded`; `recordedSpans` makes its trace (`GET /runs/{id}/trace`, kept
   current by the socket) the span tree every trace surface walks. Nothing is
   estimated: time to first token, tokens, cost, the model and what it was sent
   are the runtime's own figures, and what the runtime does not record is left
   empty rather than made up. */
import { interfaceName } from "@/shared/lib/interface-catalog";
import { runDuration } from "@/shared/lib/runs";

import type { Agent, ApiRun, ApiRunStatus, ApiSpan, ApiTrace, Run, RunInterface, RunStatus, TraceKind, TraceStep } from "@/shared/types";
import type { ModelTier } from "@/shared/types/models";
import type {
  AgentInputPayload, AssistantPayload, ChatMessage, ContentBlock, LlmInputPayload, ObservationLevel,
  RetryOutputPayload, RouteOutputPayload, Span, SpanStatus, ToolDef, ToolInputPayload, ToolOutputPayload, TokenUsage,
  TurnInputPayload,
} from "./types";

/* ------------------------------- the run ---------------------------------- */

const STATUS: Record<ApiRunStatus, RunStatus> = {
  queued: "queued",
  running: "running",
  pending: "attention", // it waits on a person: a question, or a call to approve
  success: "success",
  halted: "halted",
  error: "error",
};

/** Runs nobody typed: a schedule or an app event started them. */
const TASKS: RunInterface[] = ["schedule", "app_trigger"];

/** A run with no conversation to name it is named by where it came from: "Chat",
    "Scheduled run", "API run", "WhatsApp run"… */
const UNTITLED: Partial<Record<RunInterface, string>> = { platform: "Chat", schedule: "Scheduled run" };
const untitled = (run: ApiRun) => UNTITLED[run.interface] ?? `${interfaceName(run.interface)} run`;

/** "Today", "Yesterday", "3 days ago" — the words the run list and its chart read. */
function dayLabel(iso: string, now: number): string {
  const days = Math.floor((new Date(now).setHours(0, 0, 0, 0) - new Date(iso).setHours(0, 0, 0, 0)) / 86_400_000);
  return days <= 0 ? "Today" : days === 1 ? "Yesterday" : `${days} days ago`;
}

/** A run as the page draws it. `title` is its conversation's, when it has one. */
export function toTraceRun(run: ApiRun, title: string | null, now = Date.now()): Run {
  const status = STATUS[run.status] ?? "queued";
  const name = title || untitled(run);
  const live = status === "running" || status === "queued";
  return {
    id: run.id,
    agentId: run.agent_id,
    title: name,
    source: TASKS.includes(run.interface) ? "task" : "chat",
    sourceLabel: name,
    status,
    when: dayLabel(run.started_at, now),
    timeLabel: live ? "now" : new Date(run.started_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
    model: "",
    turns: [],
    recorded: run,
  };
}

/* ------------------------------ the trace --------------------------------- */

interface WireMessage {
  role: "system" | "user" | "assistant" | "tool";
  content?: { type: string; data?: unknown; truncated?: boolean }[];
  tool_calls?: { id: string; name: string; args?: Record<string, unknown> }[];
  tool_call_id?: string | null;
  meta?: Record<string, unknown>;
}

interface ModelInput {
  messages?: number;
  new?: WireMessage[];
  system?: string[];
  tools?: { name: string; description?: string; parameters?: { properties?: Record<string, { type?: string }>; required?: string[] } }[];
}

const textOf = (m: WireMessage | null | undefined): string =>
  (m?.content ?? []).filter((p) => p.type === "text" && typeof p.data === "string").map((p) => p.data as string).join("");

const blocksOf = (m: WireMessage): ContentBlock[] => {
  const text = textOf(m);
  const calls = (m.tool_calls ?? []).map((c): ContentBlock => ({ type: "tool_use", id: c.id, name: c.name, input: c.args ?? {} }));
  return [...(text ? [{ type: "text" as const, text }] : []), ...calls];
};

/** Messages the model was sent, chat-ML style: a tool's answer is a user
    message's `tool_result`, and answers in a row share one message. */
function toChatMessages(said: WireMessage[], into: ChatMessage[]): void {
  for (const m of said) {
    if (m.role === "tool") {
      const result: ContentBlock = { type: "tool_result", tool_use_id: m.tool_call_id ?? "", content: textOf(m) || null, ...(m.meta?.error ? { is_error: true } : {}) };
      const last = into[into.length - 1];
      if (last?.role === "user" && last.content.every((b) => b.type === "tool_result")) last.content.push(result);
      else into.push({ role: "user", content: [result] });
    } else if (m.role === "user" || m.role === "assistant") {
      into.push({ role: m.role, content: blocksOf(m) });
    }
  }
}

const toolDef = (t: NonNullable<ModelInput["tools"]>[number]): ToolDef => ({
  name: t.name,
  description: t.description ?? "",
  input_schema: {
    type: "object",
    properties: Object.fromEntries(Object.entries(t.parameters?.properties ?? {}).map(([k, v]) => [k, { type: v?.type ?? "string" }])),
    required: t.parameters?.required ?? [],
  },
});

const CUT_SHORT = "the attempt ended before this step did";

const statusOf = (s: ApiSpan): SpanStatus => (s.status === "error" ? "error" : s.status === "pending" ? "pending" : "ok");
const levelOf = (status: SpanStatus): ObservationLevel => (status === "error" ? "ERROR" : status === "pending" ? "WARNING" : "DEFAULT");
const shorten = (text: string, max = 90) => (text.length > max ? `${text.slice(0, max - 1)}…` : text);

/** The run's trace as the tree the page walks: the agent at the root, then one
    node per attempt (flattened away when there was only one), then its model
    and tool calls in the order they started. */
export function recordedSpans(trace: ApiTrace, run: Run, agent: Agent, now = Date.now()): Span {
  const at = (s: ApiSpan) => Date.parse(s.started_at);
  // what the API sent, before a cut-short step is marked failed below
  const sent = new Map(trace.spans.map((s) => [s.id, s]));
  const recordedStart = run.recorded ? Date.parse(run.recorded.started_at) : now;
  const t0 = Math.min(recordedStart, ...trace.spans.map(at));
  const durOf = (s: ApiSpan) => s.duration_ms ?? (s.status === "pending" ? Math.max(0, now - at(s)) : 0);

  // in the order the steps started, whatever order their records arrived in
  const sorted = [...trace.spans].sort((a, b) => at(a) - at(b) || (a.seq ?? 0) - (b.seq ?? 0));
  const roots = new Set(sorted.filter((s) => !s.parent_span_id).map((s) => s.id));
  // a step's attempt is its parent, or — for an event inside a model call — its grandparent
  const attemptOf = (s: ApiSpan) =>
    !s.parent_span_id ? s.id : roots.has(s.parent_span_id) ? s.parent_span_id : sorted.find((p) => p.id === s.parent_span_id)?.parent_span_id;
  // A step still pending in an attempt a later one replaced died with it; the runtime
  // saves it that way once the run ends (`trace.py::cut_short`), and says so here first.
  const live = [...roots].pop();
  const ordered = sorted.map((s) =>
    s.status === "pending" && attemptOf(s) !== live ? { ...s, status: "error" as const, output: s.output ?? { error: CUT_SHORT } } : s,
  );
  const attempts = ordered.filter((s) => !s.parent_span_id);
  const inside = attemptOf;
  let toolNames: string[] = [];

  const node = (s: ApiSpan, kind: TraceKind, fields: Partial<Span>): Span => {
    const status = statusOf(s);
    const start = at(s) - t0;
    const dur = durOf(s);
    const raw: TraceStep = { id: s.id, type: kind, label: fields.label ?? "", startMs: start, durMs: dur, status: s.status, model: s.model ?? undefined, tool: s.tool ?? undefined };
    return {
      id: s.id, kind, name: s.name ?? kind, chip: null, label: "", start, dur, status, raw, children: [],
      io: { input: s.input, output: s.output }, record: sent.get(s.id) ?? s,
      ...fields,
      meta: {
        observation_id: s.id, trace_id: run.id, type: s.obs_type, start_offset_ms: start, latency_ms: dur,
        level: levelOf(status),
        ...fields.meta,
      },
    };
  };

  const buildAttempt = (root: ApiSpan, index: number): Span => {
    const history: ChatMessage[] = [];
    let system: string | null = null;
    let tools: ToolDef[] = [];
    let lastText = "";
    // an event a fallback left inside a model call is drawn beside it, in time order
    const steps = ordered.filter((s) => s.id !== root.id && inside(s) === root.id);
    const children = steps.map((s): Span => {
      if (s.kind === "llm") {
        const input = (s.input ?? {}) as ModelInput;
        if (input.system) system = input.system.join("\n\n") || null;
        if (input.tools) {
          tools = input.tools.map(toolDef);
          toolNames = tools.map((t) => t.name);
        }
        toChatMessages(input.new ?? [], history);
        const sent: LlmInputPayload = { model: s.model ?? undefined, max_tokens: null, temperature: null, system, tools, messages: JSON.parse(JSON.stringify(history)) };
        const answer = s.output as WireMessage | null;
        if (answer) toChatMessages([answer], history);
        const text = textOf(answer);
        if (text) lastText = text;
        const usage: TokenUsage = {
          input_tokens: s.tokens_in ?? 0, output_tokens: s.tokens_out ?? 0,
          ...(s.tokens_cached ? { cache_read_input_tokens: s.tokens_cached } : {}),
          total_tokens: (s.tokens_in ?? 0) + (s.tokens_out ?? 0),
        };
        const calls = (answer?.tool_calls ?? []).map((c) => c.name);
        const failed = (s.output as { error?: string } | null)?.error;
        const tier = answer?.meta?.tier as ModelTier | undefined;  // the run's own word (hooks/tiers.mark_tier)
        return node(s, "llm", {
          name: "llm", chip: s.model,
          ...(tier ? { tier } : {}),
          label: failed ? shorten(failed) : text ? shorten(text) : calls.length ? `Calls ${calls.join(", ")}` : "Generating",
          io: {
            input: sent,
            output: answer?.role ? ({ role: "assistant", content: blocksOf(answer), stop_reason: answer.meta?.stop_reason as string | undefined, usage } satisfies AssistantPayload) : s.output,
          },
          ...(s.cost_usd != null ? { cost: s.cost_usd } : {}),
          ...(s.ttft_ms != null ? { ttft: s.ttft_ms } : {}),
          meta: {
            type: "GENERATION", model: s.model ?? undefined, ttft_ms: s.ttft_ms ?? undefined, usage,
            cost_usd: s.cost_usd ?? undefined, ...(failed ? { status_message: failed } : {}),
          } as Span["meta"],
        });
      }
      if (s.kind === "tool") {
        const input = (s.input ?? {}) as { id?: string; name?: string; args?: Record<string, unknown> };
        const reply = s.output as WireMessage | { error?: string } | null;
        const answered = reply && "role" in reply ? (reply as WireMessage) : null;
        const text = answered ? textOf(answered) : null;
        const failed = s.status === "error" ? text ?? (reply as { error?: string } | null)?.error ?? null : null;
        const app = answered?.meta?.app;
        return node(s, "tool", {
          name: s.tool ?? input.name ?? "tool", chip: typeof app === "string" ? app : null,
          label: shorten(Object.entries(input.args ?? {}).map(([k, v]) => `${k}: ${JSON.stringify(v)}`).join(", ")),
          io: {
            // the call's id is on its input from the start, so a step still running or cut short has it too
            input: { tool_use_id: answered?.tool_call_id ?? input.id, name: s.tool ?? input.name, arguments: input.args ?? {} } satisfies ToolInputPayload,
            output: (s.status === "pending" ? null : failed != null ? { error: failed } : { result: text }) satisfies ToolOutputPayload | null,
          },
          meta: { type: "TOOL", tool: s.tool ?? undefined, ...(failed ? { status_message: failed } : {}) } as Span["meta"],
        });
      }
      if (s.kind === "route") {
        const route = (s.output ?? {}) as Partial<RouteOutputPayload>;
        const sure = route.confidence != null ? `${Math.round(route.confidence * 100)}% sure` : "";
        return node(s, "route", {
          name: "router", chip: s.model, ...(route.tier ? { tier: route.tier } : {}),
          label: route.reason ?? sure,
          meta: { type: "SPAN", ...(s.model ? { model: s.model } : {}) } as Span["meta"],
        });
      }
      if (s.kind === "approval" || s.kind === "wait") {
        // the run stopped for a person: to approve a call, or to answer a question
        const asked = (s.input ?? {}) as { question?: string | null; call?: { name?: string; args?: Record<string, unknown> } | null };
        const call = asked.call ? `${asked.call.name ?? "a tool"} ${JSON.stringify(asked.call.args ?? {})}` : "";
        return node(s, "approval", {
          name: s.kind === "approval" ? "approval" : "question",
          label: shorten(asked.question || call || "Waiting on a person"),
          meta: { type: "SPAN" } as Span["meta"],
        });
      }
      if (s.kind !== "retry") {
        // a kind this screen doesn't know yet: drawn as a plain step, its record as it came
        return node(s, s.kind as TraceKind, { label: shorten(s.name ?? s.kind), meta: { type: s.obs_type } as Span["meta"] });
      }
      const reason = (s.output as { reason?: string } | null)?.reason ?? "";
      return node(s, "retry", {
        name: "retry", chip: s.name, label: shorten(reason || "A model was skipped"),
        io: { input: { reason, previous: s.name }, output: { outcome: "skipped", changed: "the next model answered" } satisfies RetryOutputPayload },
        meta: { type: "EVENT", status_message: reason } as Span["meta"],
      });
    });
    const prompt = textOf(root.input as WireMessage | null);
    const ended = root.output as { stop_reason?: string | null; error?: string | null } | null;
    return node(root, "turn", {
      name: `Attempt ${index + 1}`, label: prompt ? shorten(prompt) : "Carried on where it stopped", children,
      io: {
        input: { interface: interfaceName(run.recorded?.interface ?? "platform"), text: prompt || null, context_tokens: null } satisfies TurnInputPayload,
        output: { role: "assistant", content: [{ type: "text", text: lastText || null }] } satisfies AssistantPayload,
      },
      meta: { type: "SPAN", ...(ended?.error ? { status_message: ended.error } : ended?.stop_reason ? { status_message: `stopped: ${ended.stop_reason}` } : {}) } as Span["meta"],
    });
  };

  const turns = attempts.map(buildAttempt);
  const children = turns.length === 1 ? turns[0].children : turns;
  const total = Math.max(runDuration(run, now), ...trace.spans.map((s) => at(s) - t0 + durOf(s)));
  const rootStatus: SpanStatus = run.status === "error" ? "error" : run.status === "running" || run.status === "queued" ? "pending" : "ok";
  const answer = [...turns].reverse().map((t) => t.io.output?.content?.[0]?.text).find(Boolean) ?? null;
  const prompt = turns[0]?.io.input?.text ?? run.title;
  return {
    id: "root", kind: "agent", name: agent.name, chip: null, label: run.title,
    start: 0, dur: total, status: rootStatus, raw: run, run, children,
    attempts: attempts.map((a) => sent.get(a.id) ?? a),
    io: {
      input: { source: run.source, prompt } satisfies AgentInputPayload,
      output: { role: "assistant", content: [{ type: "text", text: answer }] } satisfies AssistantPayload,
    },
    meta: {
      observation_id: run.id, trace_id: run.id, type: "AGENT", start_offset_ms: 0, latency_ms: total,
      level: levelOf(rootStatus), source: run.source, tools: toolNames,
      ...(run.recorded?.error ? { status_message: run.recorded.error } : {}),
    },
  };
}
